import { Button, Modal, Slider } from "antd";
import { useRef, useState } from "react";
import Cropper, { Area } from "react-easy-crop";

interface ICropAvatar {
  openModalCropper: boolean;
  openModalCropperSet: (value: boolean) => void;
  avatarAfterCropFileSet: (file: File | null) => void;
  setAvatarAfterCrop: (url: string | null) => void;
  previewImageRef: React.MutableRefObject<string | null>;
  wrapperClassName?: string;
  maskStyle?: React.CSSProperties;
  banner?: boolean;
  outputsize?: number;
  square?: boolean;
}

const CropAvatar = ({
  openModalCropper,
  avatarAfterCropFileSet,
  openModalCropperSet,
  setAvatarAfterCrop,
  previewImageRef,
  maskStyle,
  wrapperClassName,
  banner,
  outputsize = 400,
  square
}: ICropAvatar) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);

  const [croppedImage, setCroppedImage] = useState<Area | null>(null);

  const createImage = () =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.setAttribute("crossOrigin", "anonymous"); // prevent CORS issues
      image.src = previewImageRef.current || "";
    });

  const onCropComplete = async (_: Area, croppedAreaPixels: Area) => {
    setCroppedImage(croppedAreaPixels);
  };

  const onSetCrop = async () => {
    const ava = await createImage();
    const canvas = document.createElement("canvas");

    // Set desired output size for better quality (e.g., 400x400)
    const outputSize = outputsize || 400;
    canvas.width = outputSize;
    canvas.height = banner ? outputSize / (4 / 1) : outputSize;
    const ctx = canvas?.getContext("2d");

    // Draw the cropped area from the image onto the canvas, scaling to output size
    ctx?.drawImage(
      ava as HTMLImageElement,
      croppedImage?.x || 0,
      croppedImage?.y || 0,
      croppedImage?.width || 0,
      croppedImage?.height || 0,
      0,
      0,
      outputSize,
      banner ? outputSize / (4 / 1) : outputSize,
    );

    // Return a blob or base64 URL
    const blobToBase64 = () => {
      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob); // or resolve(URL.createObjectURL(blob));
        }, "image/jpeg");
      });
    };

    blobToBase64().then((blob) => {
      avatarAfterCropFileSet(blob as File);
      setAvatarAfterCrop(URL.createObjectURL(blob as Blob));
      openModalCropperSet(false);
    });
  };

  return (
    <Modal
      footer={null}
      open={openModalCropper}
      onCancel={() => {
        openModalCropperSet(false);
      }}
      width={400}
      wrapClassName={wrapperClassName}
      styles={{
        mask: {
          ...maskStyle,
        },
        content: {
          padding: 0,
          paddingBottom: 16,
        },
      }}
    >
      <div className="flex items-center justify-center flex-col gap-4">
        <Cropper
          image={previewImageRef.current || ""}
          crop={crop}
          zoom={zoom}
          aspect={banner ? 4 / 1 : 4 / 4}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
          cropShape={(banner || square) ? "rect" : "round"}
          zoomSpeed={10}
          maxZoom={10}
          style={{
            containerStyle: {
              width: 400,
              height: 400,
              position: "relative",
            },
          }}
          showGrid={true}
        />
        <div className="flex flex-col items-start w-full max-w-xs">
          <p>Slide to adjust zoom</p>
          <div className="w-full max-w-xs">
            <Slider
              value={zoom}
              onChange={(e) => setZoom(e)}
              styles={{
                track: { backgroundColor: "#158284" },
                rail: { backgroundColor: "#d9d9d9" },
                handle: {
                  borderColor: "white",
                  backgroundColor: "#158284",
                  outlineColor: "#158284",
                },
              }}
              min={1}
              max={6}
              step={0.1}
              tooltip={{ open: false }}
            />
          </div>{" "}
        </div>
        <Button
          onClick={onSetCrop}
          disabled={!previewImageRef.current}
          type="primary"
        >
          Set Crop
        </Button>
      </div>
    </Modal>
  );
};

export default CropAvatar;
